import { NgModule } from '@angular/core';
import { RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { AdminComponent } from './admin/admin.component';
import { DrawingsComponent } from './admin/drawings/drawings.component';
import { FeatureFlagsComponent } from './admin/featureflags/featureflags.component';
import { IpsettingsComponent } from './admin/ipsettings/ipsettings.component';
import { ScimAdministrationComponent } from './admin/scim-administration/scim-administration.component';
import { SyncMonitoringComponent } from './admin/sync-monitoring/sync-monitoring.component';
import { AdSignInComponent } from './auth/ad-sign-in/ad-sign-in.component';
import { AdSignOutComponent } from './auth/ad-sign-out/ad-sign-out.component';
import { AdminLandingGuard } from './auth/admin-landing.guard';
import { AuthGuard } from './auth/auth-guard.guard';
import { AuthService } from './auth/auth.service';
import { IllegalGuard } from './auth/illegal-guard.guard';
import { PermissionGuard } from './auth/permission-guard.guard';
import { PrintLandingGuard } from './auth/print-landing.guard';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { TopLevelPermissionGuard } from './auth/top-level-permission.guard';
import { CalculationsComponent } from './calculations/calculations.component';
import { MatricesComponent } from './calculations/matrices/matrices.component';
import { CoreComponent } from './core/core.component';
import { DocsComponent } from './docs/docs.component';
import { MappingsComponent } from './docs/mappings/mappings.component';
import { TemplatesComponent } from './docs/templates/templates.component';
import { AccountComponent } from './insights/administration/account/account.component';
import { BudgetsComponent } from './insights/administration/budgets/budgets.component';
import { AdminReportsComponent } from './insights/administration/reports/reports.component';
import { UsersComponent } from './insights/administration/users/users.component';
import { InsightsComponent } from './insights/insights.component';
import { ReportsComponent } from './insights/reports/reports.component';
import { SlideshowComponent } from './insights/slideshow/slideshow.component';
import { InviteComponent } from './invite/invite.component';
import { LogoutComponent } from './logout/logout.component';
import { CartingConfigComponent } from './masterdata/carting/configuration/carting.config.component';
import { CartingWagonsComponent } from './masterdata/carting/wagons/carting.wagons.component';
import { CO2Component } from './masterdata/co2/co2.component';
import { BasketsComponent } from './masterdata/cutting/baskets/baskets.component';
import { ConfigsComponent } from './masterdata/cutting/configs/configs.component';
import { RulesComponent } from './masterdata/cutting/rules/rules.component';
import { SetupsComponent } from './masterdata/cutting/setups/setups.component';
import { MasterdataComponent } from './masterdata/masterdata.component';
import { PalletisingConfigurationComponent } from './masterdata/palletising/configuration/palletising.configuration.component';
import { GroupingsComponent } from './masterdata/palletising/groupings/groupings.component';
import { PalletsComponent } from './masterdata/palletising/pallets/pallets.component';
import { ZipCodeComponent } from './masterdata/palletising/zipcode/zipcode.component';
import { RackingRacksComponent } from './masterdata/racking/racks/racking.racks.component';
import { RackingRacksetsComponent } from './masterdata/racking/racksets/racking.racksets.component';
import { RackingRulesComponent } from './masterdata/racking/rules/racking.rules.component';
import { RegistersComponent } from './masterdata/registers/registers.component';
import { SingleregisterComponent } from './masterdata/singleregister/singleregister.component';
import { MesCjsTerminalRegisterComponent } from './mes-cjs/mes-cjs-terminal/mes-cjs-terminal-register/mes-cjs-terminal-register.component';
import { MesCjsTerminalComponent } from './mes-cjs/mes-cjs-terminal/mes-cjs-terminal.component';
import { MesCjsComponent } from './mes-cjs/mes-cjs.component';
import { CalendarComponent } from './mes/calendar/calendar.component';
import { IncidentCodeComponent } from './mes/incident-code/incident-code.component';
import { MesBatchComponent } from './mes/mes-batch/mes-batch.component';
import { DepartmentComponent } from './mes/mes-department/department.component';
import { EmployeeComponent } from './mes/mes-employee/employee.component';
import { MesProductionTerminalComponent } from './mes/mes-production-terminal/mes-production-terminal.component';
import { MesComponent } from './mes/mes.component';
import { ProductionNoteComponent } from './mes/production-note/production-note.component';
import { ScanningPrefixComponent } from './mes/scanning-prefix/scanning-prefix.component';
import { ShiftApprovalComponent } from './mes/shift/mes-shift-approval.component';
import { StockReorderComponent } from './mes/stock-reorder/stock-reorder.component';
import { TeamProfilesComponent } from './mes/team-profiles/team-profiles.component';
import { TerminalAdministrationComponent } from './mes/terminal-administration/terminal-administration.component';
import { TerminalTemplateAdministrationComponent } from './mes/terminaltemplate-administration/terminaltemplate-administration.component';
import { WorktasksListComponent } from './mes/worktask/worktasks-list.component';
import { PrintCollectionJobsComponent } from './print/collection-jobs/print-collection-jobs.component';
import { PrintJobsComponent } from './print/jobs/print-jobs.component';
import { PrintTestComponent } from './print/print-test/print-test.component';
import { PrintComponent } from './print/print.component';
import { PrinterAdministrationComponent } from './print/printer-administration/printer-administration.component';
import { ProductionPlanListComponent } from './production-plan/admin/list.component';
import { ProductionPlanViewComponent } from './production-plan/production-plan-view/production-plan-view.component';
import { ConfiguratorComponent } from './sales/configurator/configurator.component';
import { OrderConfigurationComponent } from './sales/orderconfiguration/orderconfiguration.component';
import { OrdersListComponent } from './sales/orderslist/orderslist.component';
import { ConfiguratorPocComponent } from './sales/poc/configurator/configuratorpoc.component';
import { OrderPocComponent } from './sales/poc/order/orderpoc.component';
import { SalesComponent } from './sales/sales.component';
import { CrossAccountOneMonitoringComponent } from './setup/cross-account-one-monitoring/cross-account-one-monitoring.component';
import { FeatureFlagsAdminComponent } from './setup/featureflagsadmin/featureflagsadmin.component';
import { SetupComponent } from './setup/setup.component';
import { ForceReloadRouteReuseStrategy } from './shared/services/force-reload-route-reuse-strategy';
import { UnknownComponent } from './unknown/unknown.component';

const routes: Routes = [
	{ path: '', redirectTo: '/signin', pathMatch: 'full' },

	{
		path: 'mes-production',
		component: MesCjsComponent,
		children: [
			{
				path: 'terminal',
				component: MesCjsTerminalComponent,
				children: [
					{
						path: 'register',
						component: MesCjsTerminalRegisterComponent,
					},
					{
						path: 'select-task',
						component: MesCjsTerminalRegisterComponent,
					},
					{
						path: 'select-task/:terminalIdentifier',
						component: MesCjsTerminalRegisterComponent,
					},
				],
			},
			{
				path: 'terminal/:terminalId',
				component: MesCjsTerminalComponent,
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'insights',
		component: InsightsComponent,
		data: {
			bs: 'AciesInsights',
			breadcrumb: {
				label: 'AciesInsights',
				url: '',
				disableUrl: false,
			},
		},
		children: [
			{
				path: 'reports',
				component: ReportsComponent,
				data: {
					bs: 'Rapporter',
					breadcrumb: {
						label: 'Rapporter',
						disableUrl: false,
					},
				},
			},
			{
				path: 'slideshow',
				component: SlideshowComponent,
				data: {
					bs: 'Slideshow',
					breadcrumb: {
						label: 'Slideshow',
						disableUrl: false,
					},
				},
			},
			{
				path: 'administration/users',
				component: UsersComponent,
				data: {
					bs: 'Brugeradgang',
					breadcrumb: {
						label: 'Administration > Brugeradgang',
						disableUrl: false,
					},
				},
			},
			{
				path: 'administration/users/:id',
				component: UsersComponent,
				data: {
					bs: 'Brugeradgang',
					breadcrumb: {
						label: 'Administration > Brugeradgang',
						disableUrl: false,
					},
				},
			},
			{
				path: 'administration/budgets',
				component: BudgetsComponent,
				data: {
					bs: 'Budget',
					breadcrumb: {
						label: 'Administration > Budget',
						disableUrl: false,
					},
				},
			},
			{
				path: 'administration/accounts',
				component: AccountComponent,
				data: {
					breadcrumb: {
						bs: 'Kontoadgang',
						label: 'Administration > Kontoadgang',
						disableUrl: false,
					},
				},
			},
			{
				path: 'administration/adminreports',
				component: AdminReportsComponent,
				data: {
					bs: 'Rapporter',
					breadcrumb: {
						label: 'Administration > Rapporter',
						disableUrl: false,
					},
				},
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'mes/terminals/:terminalId',
		component: MesProductionTerminalComponent,
		data: {},
		canActivate: [AuthGuard],
	},
	{
		path: 'mes',
		component: MesComponent,
		data: {},
		children: [
			{
				path: 'users',
				component: EmployeeComponent,
				data: {},
			},
			{
				path: 'worktasks',
				component: WorktasksListComponent,
				data: {},
			},
			{
				path: 'team-profiles',
				component: TeamProfilesComponent,
				data: {},
			},
			{
				path: 'calendar',
				component: CalendarComponent,
				data: {},
			},
			{
				path: 'terminal-administration',
				component: TerminalAdministrationComponent,
				data: { permission: ['mes:TerminalGet', 'mes:WorkTaskGet', 'mes:IncidentCodeGet'] },
				canActivate: [PermissionGuard],
			},
			{
				path: 'terminaltemplate-administration',
				component: TerminalTemplateAdministrationComponent,
				data: { permission: 'mes:TerminalGet' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'mes-production-terminal',
				component: MesProductionTerminalComponent,
				data: {},
			},
			{
				path: 'mes-batch',
				component: MesBatchComponent,
				data: {},
			},
			{
				path: 'mes-department',
				component: DepartmentComponent,
				data: {},
			},
			{
				path: 'shift-approval',
				component: ShiftApprovalComponent,
				data: {
					permission: ['mes:ShiftGet', 'mes:DepartmentGet', 'mes:EmployeeGet', 'mes:TeamProfileGet', 'mes:WorkTaskGet'],
				},
				canActivate: [PermissionGuard],
			},
			{
				path: 'stock-reorder',
				component: StockReorderComponent,
			},
			{
				path: 'scanning-prefix',
				component: ScanningPrefixComponent,
			},
			{
				path: 'production-note',
				component: ProductionNoteComponent,
			},
			{
				path: 'incident-code',
				component: IncidentCodeComponent,
			},
			{
				path: 'production-plans/:configId',
				component: ProductionPlanViewComponent,
			},
			{
				path: 'production-plan-admin',
				component: ProductionPlanListComponent,
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'print',
		component: PrintComponent,
		data: {
			bs: 'Print',
			breadcrumb: {
				label: 'Print',
				url: '',
				disableUrl: false,
			},
		},
		children: [
			{
				path: 'administration',
				component: PrinterAdministrationComponent,
				data: { permission: 'print:PrinterGet' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'test',
				component: PrintTestComponent,
				data: { permission: 'print:LabelPost' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'collection-jobs',
				component: PrintCollectionJobsComponent,
				data: { permission: 'print:LabelListGet' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'jobs',
				component: PrintJobsComponent,
				data: { permission: 'print:LabelExecutionList' },
				canActivate: [PermissionGuard],
			},
		],
		canActivate: [AuthGuard, PrintLandingGuard],
	},
	{
		path: 'sales',
		component: SalesComponent,
		data: {
			bs: 'Sales',
			breadcrumb: {
				label: 'Sales',
				url: '',
				disableUrl: false,
			},
		},
		children: [
			{
				path: 'orders',
				component: OrderPocComponent,
				data: {
					bs: 'Orders',
					breadcrumb: {
						label: 'Orders',
						disableUrl: false,
					},
				},
			},
			{
				path: 'orderslist',
				component: OrdersListComponent,
			},
			{
				path: 'configuration',
				component: OrderConfigurationComponent,
			},
			{
				path: 'configurator',
				component: ConfiguratorPocComponent,
			},
			{
				path: 'configurator_v2',
				component: ConfiguratorComponent,
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'masterdata',
		component: MasterdataComponent,
		data: {
			bs: 'Masterdata',
			breadcrumb: {
				label: 'Masterdata',
				url: '',
				disableUrl: false,
			},
		},
		children: [
			{
				path: 'registers',
				component: RegistersComponent,
				data: {
					bs: 'Registers',
					breadcrumb: {
						label: 'Registers',
						disableUrl: false,
					},
				},
			},
			{
				path: 'registers/:registerName',
				component: SingleregisterComponent,
				data: {
					bs: 'Registers',
					breadcrumb: {
						label: 'Registers',
						disableUrl: false,
					},
				},
			},
			{
				path: 'cutting/setups',
				component: SetupsComponent,
				data: {},
			},
			{
				path: 'cutting/rules',
				component: RulesComponent,
				data: {},
			},
			{
				path: 'cutting/baskets',
				component: BasketsComponent,
				data: {},
			},
			{
				path: 'cutting/configs',
				component: ConfigsComponent,
				data: {},
			},
			{
				path: 'palletising/groupings',
				component: GroupingsComponent,
				data: {},
			},
			{
				path: 'palletising/pallets',
				component: PalletsComponent,
				data: {},
			},
			{
				path: 'palletising/configuration',
				component: PalletisingConfigurationComponent,
				data: {},
			},
			{
				path: 'palletising/zipcode',
				component: ZipCodeComponent,
			},
			{
				path: 'racking/racks',
				component: RackingRacksComponent,
				data: {},
			},
			{
				path: 'racking/rack-sets',
				component: RackingRacksetsComponent,
				data: {},
			},
			{
				path: 'racking/rules',
				component: RackingRulesComponent,
				data: {},
			},
			{
				path: 'carting/wagons',
				component: CartingWagonsComponent,
				data: {},
			},
			{
				path: 'carting/config',
				component: CartingConfigComponent,
				data: {},
			},
			{
				path: 'co2',
				component: CO2Component,
				data: {},
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'docs',
		component: DocsComponent,
		data: {
			bs: 'Documents',
			breadcrumb: {
				label: 'Documents',
				url: '',
				disableUrl: false,
			},
		},
		children: [
			{
				path: 'templates',
				component: TemplatesComponent,
				data: {
					bs: 'Templates',
					breadcrumb: {
						label: 'Templates',
						disableUrl: false,
					},
				},
			},
			{
				path: 'mappings',
				component: MappingsComponent,
				data: {
					bs: 'Mappings',
					breadcrumb: {
						label: 'Mappings',
						disableUrl: false,
					},
				},
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'calculations',
		component: CalculationsComponent,
		data: {},
		children: [
			{
				path: 'matrices',
				component: MatricesComponent,
				data: {},
			},
		],
		canActivate: [AuthGuard],
	},
	{
		path: 'administration',
		component: AdminComponent,
		data: {},
		children: [
			{
				path: 'ipsettings',
				component: IpsettingsComponent,
				data: { permission: 'iam:OrganizationsAccountsPut' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'drawings',
				component: DrawingsComponent,
				data: { permission: 'drawing:StylingGetDownloadUrl' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'featureflags',
				component: FeatureFlagsComponent,
				data: { permission: 'iam:FlagsVariantPut' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'scim-administration',
				component: ScimAdministrationComponent,
				data: { permission: 'iam:ScimUsersGetList' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'sync-monitoring',
				component: SyncMonitoringComponent,
				canActivate: [PermissionGuard],
				data: { permission: 'one:EnvironmentGet' },
			},
		],
		canActivate: [AuthGuard, AdminLandingGuard],
	},
	{
		path: 'setup',
		component: SetupComponent,
		data: { permission: 'iam:OrganizationsPut' },
		children: [
			{
				path: 'featureflagsadmin',
				component: FeatureFlagsAdminComponent,
				data: { permission: 'iam:FlagsPut' },
				canActivate: [PermissionGuard],
			},
			{
				path: 'cross-account-one-monitoring',
				component: CrossAccountOneMonitoringComponent,
				data: { permission: 'one:MonitoringOverview' },
				canActivate: [TopLevelPermissionGuard],
			},
		],
		canActivate: [AuthGuard, PermissionGuard],
	},
	{ path: 'signin', component: SignInComponent },
	{ path: 'invite', component: InviteComponent, canActivate: [AuthGuard] },
	{
		path: 'core',
		component: CoreComponent,
		canActivate: [IllegalGuard],
	},
	{
		path: 'core/:id',
		component: CoreComponent,
		canActivate: [IllegalGuard],
	},
	{ path: 'adsignin', component: AdSignInComponent },
	{ path: 'adsignout', component: AdSignOutComponent },
	{ path: 'logout', component: LogoutComponent },
	{ path: '**', component: UnknownComponent },
];

@NgModule({
	imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
	exports: [RouterModule],
	providers: [AuthService, { provide: RouteReuseStrategy, useClass: ForceReloadRouteReuseStrategy }],
})
export class AppRoutingModule {}
